import { useState, useEffect } from 'react';
import { API } from 'aws-amplify';
import cdkConfig from '../config/cdk-exports';
import { analyticsCDKService } from '../services/analytics-cdk';

interface AnalyticsData {
  dailyMetrics: Array<{
    id: string;
    date: string;
    totalVisits: number;
    uniqueUsers: number;
    transcriptionJobs: number;
    successfulJobs: number;
    failedJobs: number;
    totalMinutesTranscribed: number;
    totalCost: number;
    translationJobs: number;
    createdAt: string;
    updatedAt: string;
  }>;
  userMetrics: {
    id: string;
    totalUsers: number;
    activeUsers30d: number;
    newUsersToday: number;
    retentionRate: number;
    avgSessionDuration: number;
    lastUpdated: string;
  };
  languageStats: Array<{
    id: string;
    language: string;
    transcriptionCount: number;
    translationCount: number;
    successRate: number;
    lastUpdated: string;
  }>;
  costAnalytics: {
    totalCost: number;
    costPerMinute: number;
    costSavings: number;
    period: string;
  };
  completeSummary: {
    totalJobs: number;
    successfulJobs: number;
    failedJobs: number;
    totalMinutesTranscribed: number;
    totalCost: number;
    lastUpdated: string;
    note: string;
  };
}

const getAnalyticsDashboard = `
  query GetAnalyticsDashboard($startDate: String!, $endDate: String!) {
    getAnalyticsDashboard(startDate: $startDate, endDate: $endDate) {
      dailyMetrics {
        id
        date
        totalVisits
        uniqueUsers
        transcriptionJobs
        successfulJobs
        failedJobs
        totalMinutesTranscribed
        totalCost
        translationJobs
        createdAt
        updatedAt
      }
      userMetrics {
        id
        totalUsers
        activeUsers30d
        newUsersToday
        retentionRate
        avgSessionDuration
        lastUpdated
      }
      languageStats {
        id
        language
        transcriptionCount
        translationCount
        successRate
        lastUpdated
      }
      costAnalytics {
        totalCost
        costPerMinute
        costSavings
        period
      }
      completeSummary {
        totalJobs
        successfulJobs
        failedJobs
        totalMinutesTranscribed
        totalCost
        lastUpdated
        note
      }
    }
  }
`;

export const useAnalyticsData = (startDate?: string, endDate?: string) => {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAnalytics = async (start?: string, end?: string) => {
    setLoading(true);
    setError(null);

    try {
      const defaultEnd = new Date().toISOString().split('T')[0];
      const defaultStart = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

      let analyticsData;

      if (cdkConfig.features.useAppsyncCDK) {
        analyticsData = await analyticsCDKService.getAnalyticsDashboard(
          start || defaultStart,
          end || defaultEnd
        );
      } else {
        const response = await API.graphql({
          query: getAnalyticsDashboard,
          variables: {
            startDate: start || defaultStart,
            endDate: end || defaultEnd
          }
        }) as any;

        analyticsData = response.data && response.data.getAnalyticsDashboard;
      }

      setData(analyticsData);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch analytics data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics(startDate, endDate);
  }, [startDate, endDate]);

  return {
    data,
    loading,
    error,
    refetch: () => fetchAnalytics(startDate, endDate)
  };
};
